/**
 * CoachPlaybook - Play Surface Renderer
 * Version: 0.6.0
 * Description: Accurate SVG rendering for all sport surfaces with correct proportions
 *              Supports horizontal/vertical orientation toggle
 */

import React, { useMemo } from 'react';

// Surface color themes per sport
const SURFACE_THEMES = {
  basketball: {
    surface: '#c4833e',
    surfaceAlt: '#b5742f',
    lines: '#ffffff',
    key: '#d4935e',
    rim: '#ff6b35',
    outOfBounds: '#1a1a22'
  },
  soccer: {
    surface: '#2d8a4e',
    surfaceAlt: '#267a42',
    lines: '#ffffff',
    outOfBounds: '#1a1a22'
  },
  baseball: {
    infield: '#c4833e',
    outfield: '#2d8a4e',
    outfieldAlt: '#267a42',
    dirt: '#b07030',
    lines: '#ffffff',
    bases: '#ffffff',
    outOfBounds: '#1a1a22'
  },
  football: {
    surface: '#2d8a4e',
    surfaceAlt: '#267a42',
    lines: '#ffffff',
    endzone: '#1a5c30',
    numbers: 'rgba(255,255,255,0.5)',
    outOfBounds: '#1a1a22'
  },
  volleyball: {
    surfaceLeft: '#2563eb',
    surfaceRight: '#f97316',
    lines: '#ffffff',
    net: '#333333',
    attackLine: '#ef4444',
    outOfBounds: '#1a1a22'
  }
};

// Aspect ratios for each sport (width:height as rendered vertically — long axis is vertical)
const ASPECT_RATIOS = {
  basketball: { w: 100, h: 188 },      // 50ft wide x 94ft long
  basketballHalf: { w: 100, h: 100 },   // Half court is roughly square
  soccer: { w: 100, h: 154.4 },         // 68m wide x 105m long
  baseball: { w: 100, h: 100 },          // Quarter-circle sector, square viewBox
  football: { w: 100, h: 225 },          // 53.33yd wide x 120yd long
  volleyball: { w: 100, h: 200 }         // 9m wide x 18m long
};

// ===== BASKETBALL HALF COURT =====
// ViewBox 0 0 100 100 (50ft x 47ft scaled)
const BasketballHalf = ({ theme }) => (
  <g>
    <rect x="0" y="0" width="100" height="100" fill={theme.surface} />

    {/* Boundary lines */}
    <rect x="1" y="1" width="98" height="98" fill="none" stroke={theme.lines} strokeWidth="0.5" />

    {/* Half court line (top) */}
    <line x1="1" y1="1" x2="99" y2="1" stroke={theme.lines} strokeWidth="0.5" />

    {/* Key/Paint — 16ft wide = 32%, 19ft deep from baseline = 40.4% */}
    <rect x="34" y="59.6" width="32" height="40.4" fill={theme.key} stroke={theme.lines} strokeWidth="0.4" />

    {/* Free throw line */}
    <line x1="34" y1="59.6" x2="66" y2="59.6" stroke={theme.lines} strokeWidth="0.4" />

    {/* Free throw circle — 6ft radius = 12% */}
    <circle cx="50" cy="59.6" r="12" fill="none" stroke={theme.lines} strokeWidth="0.4" />

    {/* Three-point arc — 23.75ft = 47.5% radius from basket */}
    {/* Basket at (50, 88.8). Corner 3 at 22ft from basket = y=6 from sideline */}
    <path
      d="M 6 100 L 6 60 A 47.5 47.5 0 0 1 94 60 L 94 100"
      fill="none" stroke={theme.lines} strokeWidth="0.35"
    />

    {/* Restricted area — 4ft radius = 8% from basket */}
    <path
      d="M 42 100 A 8 8 0 0 1 58 100"
      fill="none" stroke={theme.lines} strokeWidth="0.3" strokeDasharray="1.5 1"
    />

    {/* Backboard — 6ft wide = 12%, 4ft from baseline */}
    <line x1="44" y1="91.5" x2="56" y2="91.5" stroke={theme.lines} strokeWidth="0.6" />

    {/* Rim — 18in diameter = 3% */}
    <circle cx="50" cy="89" r="1.5" fill="none" stroke={theme.rim} strokeWidth="0.4" />
    <circle cx="50" cy="89" r="0.3" fill={theme.rim} />

    {/* Hash marks on key */}
    {[67, 72, 77, 82].map((y, i) => (
      <g key={`hash-${i}`}>
        <line x1="31" y1={y} x2="34" y2={y} stroke={theme.lines} strokeWidth="0.3" />
        <line x1="66" y1={y} x2="69" y2={y} stroke={theme.lines} strokeWidth="0.3" />
      </g>
    ))}
  </g>
);

// ===== BASKETBALL FULL COURT =====
const BasketballFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.basketball;
  return (
    <g>
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.surface} />
      <rect x="1" y="1" width={vb.w - 2} height={vb.h - 2} fill="none" stroke={theme.lines} strokeWidth="0.4" />

      {/* Center line and circle */}
      <line x1="1" y1={vb.h / 2} x2={vb.w - 1} y2={vb.h / 2} stroke={theme.lines} strokeWidth="0.3" />
      <circle cx={vb.w / 2} cy={vb.h / 2} r="12" fill="none" stroke={theme.lines} strokeWidth="0.3" />

      {/* Top half — paint, FT, 3pt, rim */}
      <rect x="34" y="1" width="32" height="40" fill={theme.key} stroke={theme.lines} strokeWidth="0.3" />
      <circle cx="50" cy="41" r="12" fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <line x1="44" y1="9" x2="56" y2="9" stroke={theme.lines} strokeWidth="0.5" />
      <circle cx="50" cy="11" r="1.5" fill="none" stroke={theme.rim} strokeWidth="0.3" />
      <path d="M 6 1 L 6 55 A 47.5 47.5 0 0 0 94 55 L 94 1" fill="none" stroke={theme.lines} strokeWidth="0.3" />

      {/* Bottom half (mirrored) */}
      <rect x="34" y={vb.h - 41} width="32" height="40" fill={theme.key} stroke={theme.lines} strokeWidth="0.3" />
      <circle cx="50" cy={vb.h - 41} r="12" fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <line x1="44" y1={vb.h - 9} x2="56" y2={vb.h - 9} stroke={theme.lines} strokeWidth="0.5" />
      <circle cx="50" cy={vb.h - 11} r="1.5" fill="none" stroke={theme.rim} strokeWidth="0.3" />
      <path d={`M 6 ${vb.h - 1} L 6 ${vb.h - 55} A 47.5 47.5 0 0 1 94 ${vb.h - 55} L 94 ${vb.h - 1}`} fill="none" stroke={theme.lines} strokeWidth="0.3" />
    </g>
  );
};

// ===== SOCCER PITCH =====
// ViewBox 0 0 100 154.4 (68m wide x 105m long, 1m = 1.4706 units)
const SoccerFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.soccer;
  const s = 1.4706; // scale: 1m = s units
  return (
    <g>
      {/* Pitch surface with mow stripes */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.surface} />
      {Array.from({ length: 10 }, (_, i) => (
        i % 2 === 0 ? <rect key={`s${i}`} x="0" y={i * (vb.h / 10)} width={vb.w} height={vb.h / 10} fill={theme.surfaceAlt} opacity="0.2" /> : null
      ))}

      {/* Boundary */}
      <rect x="3" y="3" width="94" height={vb.h - 6} fill="none" stroke={theme.lines} strokeWidth="0.4" />

      {/* Center line */}
      <line x1="3" y1={vb.h / 2} x2="97" y2={vb.h / 2} stroke={theme.lines} strokeWidth="0.3" />

      {/* Center circle — 9.15m radius = 13.46 units */}
      <circle cx="50" cy={vb.h / 2} r={9.15 * s} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <circle cx="50" cy={vb.h / 2} r="0.6" fill={theme.lines} />

      {/* Top penalty area — 40.32m wide = 59.3u, 16.5m deep = 24.3u */}
      <rect x={50 - 29.65} y="3" width={59.3} height={16.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      {/* Top goal area — 18.32m wide = 26.9u, 5.5m deep = 8.1u */}
      <rect x={50 - 13.47} y="3" width={26.94} height={5.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      {/* Top penalty spot — 11m = 16.2u from goal line */}
      <circle cx="50" cy={3 + 11 * s} r="0.5" fill={theme.lines} />
      {/* Top penalty arc — 9.15m radius from penalty spot, outside penalty area */}
      <path d={`M ${50 - 10.77} ${3 + 16.5 * s} A ${9.15 * s} ${9.15 * s} 0 0 0 ${50 + 10.77} ${3 + 16.5 * s}`} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      {/* Top goal */}
      <rect x={50 - 5.38} y="0.5" width={10.76} height="2.5" fill="none" stroke={theme.lines} strokeWidth="0.5" />

      {/* Bottom penalty area (mirrored) */}
      <rect x={50 - 29.65} y={vb.h - 3 - 16.5 * s} width={59.3} height={16.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <rect x={50 - 13.47} y={vb.h - 3 - 5.5 * s} width={26.94} height={5.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <circle cx="50" cy={vb.h - 3 - 11 * s} r="0.5" fill={theme.lines} />
      <path d={`M ${50 - 10.77} ${vb.h - 3 - 16.5 * s} A ${9.15 * s} ${9.15 * s} 0 0 1 ${50 + 10.77} ${vb.h - 3 - 16.5 * s}`} fill="none" stroke={theme.lines} strokeWidth="0.3" />
      <rect x={50 - 5.38} y={vb.h - 3} width={10.76} height="2.5" fill="none" stroke={theme.lines} strokeWidth="0.5" />

      {/* Corner arcs — 1m = 1.47u */}
      <path d={`M 3 ${3 + 1.47} A 1.47 1.47 0 0 1 ${3 + 1.47} 3`} fill="none" stroke={theme.lines} strokeWidth="0.25" />
      <path d={`M ${97 - 1.47} 3 A 1.47 1.47 0 0 1 97 ${3 + 1.47}`} fill="none" stroke={theme.lines} strokeWidth="0.25" />
      <path d={`M ${3 + 1.47} ${vb.h - 3} A 1.47 1.47 0 0 1 3 ${vb.h - 3 - 1.47}`} fill="none" stroke={theme.lines} strokeWidth="0.25" />
      <path d={`M 97 ${vb.h - 3 - 1.47} A 1.47 1.47 0 0 1 ${97 - 1.47} ${vb.h - 3}`} fill="none" stroke={theme.lines} strokeWidth="0.25" />
    </g>
  );
};

// ===== BASEBALL DIAMOND =====
// ViewBox 0 0 100 100, home plate at (50, 90), 1ft = 0.2 units
const BaseballFull = ({ theme }) => {
  const hp = { x: 50, y: 90 }; // Home plate
  const sc = 0.2; // 1ft = 0.2 units
  // Base positions (90ft basepaths, diamond rotated 45°)
  const b1 = { x: hp.x + 90 * sc * 0.7071, y: hp.y - 90 * sc * 0.7071 }; // 62.73, 77.27
  const b2 = { x: hp.x, y: hp.y - 90 * sc * 1.4142 };                      // 50, 64.54
  const b3 = { x: hp.x - 90 * sc * 0.7071, y: hp.y - 90 * sc * 0.7071 }; // 37.27, 77.27
  const mound = { x: 50, y: hp.y - 60.5 * sc };                            // 50, 77.9

  return (
    <g>
      {/* Outfield grass */}
      <rect x="0" y="0" width="100" height="100" fill={theme.outfield} />

      {/* Outfield fence arc — ~330-400ft */}
      <path d={`M 3 ${hp.y - 330 * sc * 0.7071} Q 50 8 ${100 - 3} ${hp.y - 330 * sc * 0.7071}`}
        fill="none" stroke={theme.lines} strokeWidth="0.5" strokeDasharray="2 1" />

      {/* Warning track */}
      <path d={`M 6 ${hp.y - 310 * sc * 0.7071} Q 50 12 ${100 - 6} ${hp.y - 310 * sc * 0.7071}`}
        fill="none" stroke={theme.dirt} strokeWidth="4" opacity="0.25" />

      {/* Infield dirt circle */}
      <circle cx="50" cy="75" r="22" fill={theme.infield} />

      {/* Grass cutout in infield (between basepaths and mound) */}
      <circle cx={mound.x} cy={mound.y} r="8" fill={theme.outfield} />

      {/* Diamond basepaths */}
      <polygon
        points={`${hp.x},${hp.y} ${b1.x},${b1.y} ${b2.x},${b2.y} ${b3.x},${b3.y}`}
        fill="none" stroke={theme.lines} strokeWidth="0.4"
      />

      {/* Foul lines extending to outfield */}
      <line x1={hp.x} y1={hp.y} x2="3" y2={hp.y - 87 * sc / 0.7071} stroke={theme.lines} strokeWidth="0.3" />
      <line x1={hp.x} y1={hp.y} x2="97" y2={hp.y - 87 * sc / 0.7071} stroke={theme.lines} strokeWidth="0.3" />

      {/* Pitcher's mound */}
      <circle cx={mound.x} cy={mound.y} r={9 * sc} fill={theme.dirt} stroke={theme.lines} strokeWidth="0.2" />
      {/* Rubber */}
      <rect x={mound.x - 1} y={mound.y - 0.3} width="2" height="0.6" fill={theme.lines} rx="0.1" />

      {/* Home plate (pentagon) */}
      <polygon points={`${hp.x},${hp.y + 0.8} ${hp.x - 1.2},${hp.y - 0.2} ${hp.x - 0.85},${hp.y - 1} ${hp.x + 0.85},${hp.y - 1} ${hp.x + 1.2},${hp.y - 0.2}`}
        fill={theme.bases} stroke={theme.lines} strokeWidth="0.2" />

      {/* Bases */}
      <rect x={b1.x - 0.8} y={b1.y - 0.8} width="1.6" height="1.6" fill={theme.bases} stroke={theme.lines} strokeWidth="0.15" transform={`rotate(45,${b1.x},${b1.y})`} />
      <rect x={b2.x - 0.8} y={b2.y - 0.8} width="1.6" height="1.6" fill={theme.bases} stroke={theme.lines} strokeWidth="0.15" transform={`rotate(45,${b2.x},${b2.y})`} />
      <rect x={b3.x - 0.8} y={b3.y - 0.8} width="1.6" height="1.6" fill={theme.bases} stroke={theme.lines} strokeWidth="0.15" transform={`rotate(45,${b3.x},${b3.y})`} />

      {/* Batter's boxes */}
      <rect x={hp.x - 2.8} y={hp.y - 1.5} width="1.6" height="3" fill="none" stroke={theme.lines} strokeWidth="0.15" />
      <rect x={hp.x + 1.2} y={hp.y - 1.5} width="1.6" height="3" fill="none" stroke={theme.lines} strokeWidth="0.15" />

      {/* On-deck circles */}
      <circle cx={hp.x - 12} cy={hp.y + 3} r="1.5" fill="none" stroke={theme.lines} strokeWidth="0.15" />
      <circle cx={hp.x + 12} cy={hp.y + 3} r="1.5" fill="none" stroke={theme.lines} strokeWidth="0.15" />

      {/* Coach's boxes */}
      <rect x={hp.x - 20} y={hp.y - 5} width="2" height="8" fill="none" stroke={theme.lines} strokeWidth="0.1" strokeDasharray="1 0.5" />
      <rect x={hp.x + 18} y={hp.y - 5} width="2" height="8" fill="none" stroke={theme.lines} strokeWidth="0.1" strokeDasharray="1 0.5" />
    </g>
  );
};

// ===== FOOTBALL FIELD =====
// ViewBox 0 0 100 225 (53.33yd wide x 120yd long, 1yd = 1.875 units)
const FootballFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.football;
  const yd = 1.875; // 1 yard in viewBox units
  const ezDepth = 10 * yd; // End zone depth
  const fieldStart = ezDepth; // Where the 0 yard line starts

  return (
    <g>
      {/* Field surface */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.surface} />

      {/* Yard stripe pattern */}
      {Array.from({ length: 12 }, (_, i) => (
        i % 2 === 0 ? <rect key={`fs${i}`} x="0" y={fieldStart + i * 10 * yd} width={vb.w} height={10 * yd} fill={theme.surfaceAlt} opacity="0.15" /> : null
      ))}

      {/* End zones */}
      <rect x="2" y="2" width="96" height={ezDepth - 2} fill={theme.endzone} stroke={theme.lines} strokeWidth="0.4" />
      <rect x="2" y={vb.h - ezDepth} width="96" height={ezDepth - 2} fill={theme.endzone} stroke={theme.lines} strokeWidth="0.4" />

      {/* Boundary */}
      <rect x="2" y="2" width="96" height={vb.h - 4} fill="none" stroke={theme.lines} strokeWidth="0.4" />

      {/* Goal lines */}
      <line x1="2" y1={fieldStart} x2="98" y2={fieldStart} stroke={theme.lines} strokeWidth="0.5" />
      <line x1="2" y1={vb.h - ezDepth} x2="98" y2={vb.h - ezDepth} stroke={theme.lines} strokeWidth="0.5" />

      {/* Yard lines every 5 yards */}
      {Array.from({ length: 21 }, (_, i) => {
        const y = fieldStart + i * 5 * yd;
        return <line key={`yl${i}`} x1="2" y1={y} x2="98" y2={y} stroke={theme.lines} strokeWidth={i % 2 === 0 ? 0.3 : 0.15} />;
      })}

      {/* Yard numbers */}
      {[10, 20, 30, 40, 50, 40, 30, 20, 10].map((num, i) => {
        const y = fieldStart + (i + 1) * 10 * yd + 5 * yd;
        return (
          <g key={`yn${i}`}>
            <text x="12" y={y} fill={theme.numbers} fontSize="4" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">{num}</text>
            <text x="88" y={y} fill={theme.numbers} fontSize="4" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">{num}</text>
          </g>
        );
      })}

      {/* Hash marks (NFL — 18.5ft = 6.17yd from center) */}
      {Array.from({ length: 100 }, (_, i) => {
        const y = fieldStart + i * yd;
        return (
          <g key={`hm${i}`}>
            <line x1="38" y1={y} x2="40" y2={y} stroke={theme.lines} strokeWidth="0.1" />
            <line x1="60" y1={y} x2="62" y2={y} stroke={theme.lines} strokeWidth="0.1" />
          </g>
        );
      })}

      {/* Goal posts */}
      <line x1="44" y1="2" x2="56" y2="2" stroke={theme.lines} strokeWidth="0.4" />
      <line x1="44" y1={vb.h - 2} x2="56" y2={vb.h - 2} stroke={theme.lines} strokeWidth="0.4" />
    </g>
  );
};

// ===== VOLLEYBALL COURT =====
// ViewBox 0 0 100 200 (9m wide x 18m long, 1m = 11.111 units)
const VolleyballFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.volleyball;
  const m = 11.111; // 1 meter in viewBox units
  const attackDist = 3 * m; // 3m from center = 33.33 units

  return (
    <g>
      {/* Court surface — two colors for each side */}
      <rect x="5" y="5" width="90" height={vb.h / 2 - 5} fill={theme.surfaceLeft} />
      <rect x="5" y={vb.h / 2} width="90" height={vb.h / 2 - 5} fill={theme.surfaceRight} />

      {/* Surrounding free zone */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="none" />

      {/* Court boundary */}
      <rect x="5" y="5" width="90" height={vb.h - 10} fill="none" stroke={theme.lines} strokeWidth="0.5" />

      {/* Center line */}
      <line x1="5" y1={vb.h / 2} x2="95" y2={vb.h / 2} stroke={theme.lines} strokeWidth="0.5" />

      {/* Net */}
      <line x1="2" y1={vb.h / 2} x2="98" y2={vb.h / 2} stroke={theme.net} strokeWidth="1.2" />
      {/* Net posts */}
      <circle cx="2" cy={vb.h / 2} r="1" fill={theme.net} />
      <circle cx="98" cy={vb.h / 2} r="1" fill={theme.net} />
      {/* Antennas */}
      <line x1="5" y1={vb.h / 2 - 3} x2="5" y2={vb.h / 2 + 3} stroke="#ff0000" strokeWidth="0.5" />
      <line x1="95" y1={vb.h / 2 - 3} x2="95" y2={vb.h / 2 + 3} stroke="#ff0000" strokeWidth="0.5" />

      {/* Attack lines (3m from center each side) */}
      <line x1="5" y1={vb.h / 2 - attackDist} x2="95" y2={vb.h / 2 - attackDist} stroke={theme.attackLine} strokeWidth="0.4" />
      <line x1="5" y1={vb.h / 2 + attackDist} x2="95" y2={vb.h / 2 + attackDist} stroke={theme.attackLine} strokeWidth="0.4" />

      {/* Zone labels (subtle) — Top team */}
      <text x="78" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">4</text>
      <text x="50" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">3</text>
      <text x="22" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">2</text>
      <text x="78" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">5</text>
      <text x="50" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">6</text>
      <text x="22" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">1</text>

      {/* Bottom team (mirrored) */}
      <text x="22" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">4</text>
      <text x="50" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">3</text>
      <text x="78" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">2</text>
      <text x="22" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">5</text>
      <text x="50" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">6</text>
      <text x="78" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.15" textAnchor="middle" fontFamily="sans-serif">1</text>
    </g>
  );
};

// ===== SURFACE REGISTRY =====
const SURFACE_RENDERERS = {
  basketball: { half: BasketballHalf, full: BasketballFull },
  soccer: { full: SoccerFull, half: SoccerFull },
  baseball: { 'full-field': BaseballFull, infield: BaseballFull, diamond: BaseballFull },
  football: { full: FootballFull, 'red-zone': FootballFull, 'goal-line': FootballFull, formation: FootballFull },
  volleyball: { full: VolleyballFull, 'half-offense': VolleyballFull, 'half-defense': VolleyballFull }
};

/**
 * PlaySurfaceRenderer — Universal sport surface renderer
 * Renders accurate SVG playing surfaces for any sport with correct proportions
 */
const PlaySurfaceRenderer = ({
  sport = 'basketball',
  view = 'half',
  orientation = 'vertical',
  width,
  height,
  customTheme = null,
  showGrid = false,
  onClick,
  children
}) => {
  const theme = useMemo(() => {
    const base = SURFACE_THEMES[sport] || SURFACE_THEMES.basketball;
    return customTheme ? { ...base, ...customTheme } : base;
  }, [sport, customTheme]);

  const SurfaceComponent = useMemo(() => {
    const sportRenderers = SURFACE_RENDERERS[sport] || SURFACE_RENDERERS.basketball;
    return sportRenderers[view] || Object.values(sportRenderers)[0];
  }, [sport, view]);

  // Get the correct aspect ratio for this sport/view
  const aspectKey = sport === 'basketball' && view === 'half' ? 'basketballHalf' : sport;
  const aspect = ASPECT_RATIOS[aspectKey] || ASPECT_RATIOS.basketballHalf;

  const isHorizontal = orientation === 'horizontal';

  // For horizontal, swap w/h in the viewBox
  const vbW = isHorizontal ? aspect.h : aspect.w;
  const vbH = isHorizontal ? aspect.w : aspect.h;
  const viewBox = `0 0 ${aspect.w} ${aspect.h}`;

  // Calculate display dimensions preserving aspect ratio
  const aspectRatio = aspect.w / aspect.h;
  let displayW = width;
  let displayH = height;
  if (width && !height) {
    displayH = isHorizontal ? width * aspectRatio : width / aspectRatio;
  } else if (height && !width) {
    displayW = isHorizontal ? height / aspectRatio : height * aspectRatio;
  } else if (!width && !height) {
    displayW = 400;
    displayH = isHorizontal ? 400 * aspectRatio : 400 / aspectRatio;
  }

  const transform = isHorizontal ? `rotate(-90 ${aspect.w / 2} ${aspect.h / 2})` : undefined;

  return (
    <svg
      viewBox={viewBox}
      width={displayW}
      height={displayH}
      style={{ userSelect: 'none', touchAction: 'none', maxWidth: '100%', height: 'auto' }}
      onClick={onClick}
    >
      <g transform={transform}>
        <rect x="0" y="0" width={aspect.w} height={aspect.h} fill={theme.outOfBounds || '#1a1a22'} />
        <SurfaceComponent theme={theme} />
        {showGrid && (
          <g opacity="0.12">
            {Array.from({ length: 11 }, (_, i) => (
              <g key={`grid-${i}`}>
                <line x1={i * (aspect.w / 10)} y1="0" x2={i * (aspect.w / 10)} y2={aspect.h} stroke="#fff" strokeWidth="0.15" />
                <line x1="0" y1={i * (aspect.h / 10)} x2={aspect.w} y2={i * (aspect.h / 10)} stroke="#fff" strokeWidth="0.15" />
              </g>
            ))}
          </g>
        )}
        {children}
      </g>
    </svg>
  );
};

export { SURFACE_THEMES, SURFACE_RENDERERS, ASPECT_RATIOS };
export default PlaySurfaceRenderer;
